import { HeartHandshake, Eye, Brain, Accessibility as A11yIcon } from "lucide-react";

const pillars = [
  {
    icon: Eye,
    title: "Built for users with visibility challenges",
    body:
      "Works alongside screen readers, zoom, and keyboard control — with spoken guidance, high-contrast states, and confirmations at every step.",
  },
  {
    icon: Brain,
    title: "Reduces cognitive load",
    body:
      "One input, one goal. EqualLayer absorbs the complexity of the page so people stay with their intent, not the interface.",
  },
  {
    icon: HeartHandshake,
    title: "Simplifies complex interfaces",
    body:
      "Cluttered forms, hidden menus, nested options — collapsed into a single clear command. No feature is more than a sentence away.",
  },
];

const Accessibility = () => {
  return (
    <section
      id="accessibility"
      data-testid="accessibility-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06] overflow-hidden"
    >
      {/* side image with overlay */}
      <div
        className="absolute inset-y-0 right-0 w-1/2 opacity-40 pointer-events-none hidden lg:block"
        aria-hidden="true"
      >
        <img
          src="https://images.pexels.com/photos/7265145/pexels-photo-7265145.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-16 items-start">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
            <A11yIcon className="h-3 w-3 text-indigo-300" strokeWidth={2} />
            <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-400">
              Accessibility first
            </span>
          </div>

          <h2 className="mt-6 font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl text-balance">
            Accessibility isn't a setting.{" "}
            <span className="text-indigo-300">It's the foundation.</span>
          </h2>

          <p className="mt-6 font-body text-zinc-400 text-base md:text-lg leading-relaxed max-w-xl">
            Equal is designed first for the people the web has overlooked. Every interaction is built to
            remove friction for users with visibility, motor, and cognitive needs — and to stay out of the
            way for everyone else.
          </p>

          <div className="mt-10 space-y-3">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  data-testid={`pillar-${i}`}
                  className="group flex gap-5 rounded-xl border border-white/[0.06] bg-white/[0.015] p-5 transition-all hover:border-indigo-500/40 hover:bg-white/[0.03]"
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center text-indigo-300 group-hover:bg-indigo-500/15 transition-all">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium text-white tracking-tight">
                      {p.title}
                    </h3>
                    <p className="mt-1.5 font-body text-[14.5px] leading-relaxed text-zinc-400">
                      {p.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* stats / quote card */}
        <div className="lg:pl-10">
          <div className="relative rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 backdrop-blur-xl p-8 md:p-10">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-indigo-500/20 via-transparent to-violet-500/20 -z-10 blur-xl" />
            <span className="font-mono uppercase tracking-[0.2em] text-[10.5px] text-zinc-500">
              Why this matters
            </span>
            <p className="mt-4 font-display text-white text-2xl md:text-3xl tracking-tight leading-snug">
              “An estimated{" "}
              <span className="text-indigo-300">1.3 billion people</span> live with a significant
              disability. Most of the web still treats accessibility as an edge case.”
            </p>
            <div className="mt-6 flex items-center gap-3 pt-6 border-t border-white/[0.06]">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500" />
              <div className="font-body">
                <div className="text-sm text-white">World Health Organization</div>
                <div className="text-xs text-zinc-500">Global report on disability, 2023</div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { v: "2.2B", l: "vision-impaired" },
                { v: "WCAG", l: "2.2 aligned" },
                { v: "< 90ms", l: "intent latency" },
              ].map((s, i) => (
                <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="font-display text-lg md:text-xl text-white">{s.v}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Accessibility;
